import React, { PropsWithChildren } from "react";

import { useWorkOSAuth } from "@worktango/d2c-service/workOSReact";
import { useBooleanFlagValue } from "@worktango/feature-flags-react";

import { Button, Flex, Heading, Icon, Spacer } from "@kazoohr/confetti";
import { coalesce, coalesceFirstTruthy, doNothing } from "@kazoohr/helpers";
import { Link, useLocation } from "@kazoohr/router";

import {
  GetCurrentUserQuery,
  useGetCurrentUserQuery,
} from "../../../generated/apiHooks";
import { PoweredBy } from "../../static/PoweredBy";
import { DevAuthTogglePlatformAdmin } from "../devAuth/DevAuthTogglePlatformAdmin";
import { ViewingAsBar } from "../ViewingAsBar";

import styles from "./constellationWrapper.scss";

export function ConstellationWrapper({ children }: PropsWithChildren) {
  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <ViewingAsBar />
      <ConstellationHeader />
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        style={{
          width: "100%",
          padding: "0px 40px 40px",
          minHeight: "calc(100vh - 150px)",
        }}
      >
        <div className={styles["constellation-wrapper"]}>{children}</div>
        <Flex justifyContent="center" style={{ marginTop: "40px" }}>
          <PoweredBy />
        </Flex>
      </Flex>
    </div>
  );
}

function getName(
  user?: null | {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  }
) {
  const name = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return coalesceFirstTruthy(name, user?.email);
}

function ConstellationHeader() {
  const { state, user } = useWorkOSAuth();

  return (
    <div className={styles["constellation-header"]}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className={styles["constellation-header-inner"]}
      >
        <Link style={{ color: "inherit" }} to="/">
          <Flex alignItems="center" gap="16px">
            <Icon icon="ai_general" size={32} variant="filled" />
            <Heading level="2">Constellation</Heading>
          </Flex>
        </Link>
        <Flex gap="24px" justifyContent="flex-end" alignItems="center">
          {state !== "loading" && user ? (
            <AuthenticatedHeaderContent />
          ) : (
            <UnauthenticatedHeaderContent />
          )}
        </Flex>
      </Flex>
      <DevAuthTogglePlatformAdmin />
    </div>
  );
}

function isPlatformAdmin(
  currentUser?: GetCurrentUserQuery["currentUser"] | null
) {
  return coalesce(currentUser?.isPlatformAdmin, false);
}

function isOrgAdmin(currentUser?: GetCurrentUserQuery["currentUser"] | null) {
  return coalesce(
    currentUser?.organizationMemberships.some((org) => org.role === "ADMIN"),
    false
  );
}

function AuthenticatedHeaderContent() {
  const { user, signOut } = useWorkOSAuth();
  const { data } = useGetCurrentUserQuery({
    onError: doNothing, // Handled elsewhere
  });
  const userName = getName(user);
  const { pathname } = useLocation();
  const isOnOrgAdminPage =
    pathname.includes("/organization") && !pathname.includes("/admin");
  const isOnPlatformAdminPage = pathname.includes("/admin");

  return (
    <>
      <div>{userName}</div>
      <Spacer size="small" orientation="horizontal" />
      {isPlatformAdmin(data?.currentUser) && (
        <Button
          data-testid="platform-admin-button"
          to="/admin"
          linkElement={Link}
          variant={isOnPlatformAdminPage ? "link" : "system"}
          icon="admin"
        >
          Platform Admin
        </Button>
      )}
      {isOrgAdmin(data?.currentUser) && (
        <Button
          data-testid="admin-button"
          to="/organization"
          linkElement={Link}
          variant={isOnOrgAdminPage ? "link" : "system"}
        >
          Admin
        </Button>
      )}
      <Button onClick={signOut} variant="system">
        Sign Out
      </Button>
    </>
  );
}

function UnauthenticatedHeaderContent() {
  const { signIn, signUp } = useWorkOSAuth();
  const isSignUpAllowed = useBooleanFlagValue("allow-signup", false);

  return (
    <>
      <Button onClick={signIn} variant="system">
        Sign In
      </Button>
      {isSignUpAllowed && (
        <Button onClick={signUp} variant="primary">
          Sign Up
        </Button>
      )}
    </>
  );
}
